import { useEffect, useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { EmptyTableMessage } from "@volca/ui/components/ui/empty-table-message";
import { Frame } from "@volca/ui/components/ui/frame";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { cn } from "@volca/ui/lib/utils";
import { useInView } from "@/core/hooks/use-in-view";
import type { ReportValue } from "@/profitability/types";
import { rowsLabel } from "./document-entries";
import type { PreviewRow } from "./types";
import type { PreviewTarget } from "./use-preview-target";

const isBlank = (value: ReportValue | undefined) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && !value.trim());

/** A column whose every filled cell is a number: right-aligned, tabular. */
const numericColumns = (columns: string[], rows: PreviewRow[]) =>
  columns.map((_, index) => {
    const filled = rows
      .map((row) => row.values[index])
      .filter((value) => !isBlank(value));
    return (
      filled.length > 0 && filled.every((value) => typeof value === "number")
    );
  });

/**
 * A document's rows as they were read, numbered by their place in the
 * table. A blank cell is tinted, never filled in: it is where a figure the
 * report needed was not in the file. The next page loads as the reader
 * reaches the end, or from the button under the rows. A cited cell (and its
 * row) is outlined and scrolled to once it is among the rows.
 */
export const PreviewTable = ({
  columns,
  rows,
  total,
  rowsCount,
  filtered,
  blankColumn,
  target = null,
  onShowAll,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  columns: string[];
  rows: PreviewRow[];
  /** Rows matching the filters, across every page. */
  total: number;
  /** Rows in the table. */
  rowsCount: number;
  filtered: boolean;
  /** The column the table is narrowed to the blanks of. */
  blankColumn: string | null;
  /** The cell a citation points to. */
  target?: PreviewTarget | null;
  onShowAll: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) => {
  const [sentinel, nearEnd] = useInView<HTMLTableRowElement>({
    rootMargin: "400px",
  });
  useEffect(() => {
    if (nearEnd && hasMore && !loadingMore) onLoadMore();
  }, [nearEnd, hasMore, loadingMore, onLoadMore]);
  const numeric = numericColumns(columns, rows);
  const targetRef = useRef<HTMLElement | null>(null);
  const scrolledTo = useRef<string | null>(null);
  const targetKey = target ? `${target.row}:${target.column}` : null;
  const targetShown = !!target && rows.some((row) => row.number === target.row);
  useEffect(() => {
    if (!targetShown || scrolledTo.current === targetKey) return;
    scrolledTo.current = targetKey;
    targetRef.current?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [targetShown, targetKey]);

  return (
    <Frame
      spacing="xs"
      // As tall as its rows, never taller than the space it is given:
      // past that it scrolls inside, header held in place.
      className="w-full min-w-0 shrink max-lg:max-h-128 lg:min-h-0"
    >
      <Table variant="card" scroll aria-label="Rows of the file">
        <TableHeader>
          <TableRow>
            <TableHead scope="col" className="w-12 text-right tabular-nums">
              <span className="sr-only">Row</span>#
            </TableHead>
            {columns.map((column, index) => (
              <TableHead
                key={`${index}:${column}`}
                scope="col"
                className={cn({
                  "text-right": numeric[index],
                  "text-ink": column === blankColumn,
                })}
              >
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const targetRow = target?.row === row.number;
            return (
              <TableRow
                key={row.number}
                ref={
                  targetRow && target?.column === null
                    ? (node) => {
                        targetRef.current = node;
                      }
                    : undefined
                }
                aria-current={targetRow ? "true" : undefined}
                className={cn({ "bg-selected!": targetRow })}
              >
                <TableCell className="text-right text-subtlest tabular-nums">
                  {row.number}
                </TableCell>
                {columns.map((column, index) => {
                  const value = row.values[index];
                  const blank = isBlank(value);
                  const cited = targetRow && target?.column === index;
                  return (
                    <TableCell
                      key={`${index}:${column}`}
                      ref={
                        cited
                          ? (node) => {
                              targetRef.current = node;
                            }
                          : undefined
                      }
                      title={blank ? "Blank" : undefined}
                      data-cited={cited || undefined}
                      className={cn("max-w-80 truncate", {
                        "text-right tabular-nums": numeric[index],
                        "bg-warning-subtle!": blank,
                        "outline-2 -outline-offset-2 outline-information-bold":
                          cited,
                      })}
                    >
                      {blank ? (
                        <span className="sr-only">Blank</span>
                      ) : (
                        String(value)
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          {hasMore && (
            <tr ref={sentinel} aria-hidden>
              <td colSpan={columns.length + 1} className="h-px p-0" />
            </tr>
          )}
        </TableBody>
      </Table>
      {!rows.length && <EmptyTableMessage>No rows match.</EmptyTableMessage>}
      <Stack
        direction="row"
        wrap
        align="center"
        justify="between"
        gap={2}
        className="px-2 py-1 text-xs text-subtle"
      >
        <span className="inline-flex items-center gap-2 tabular-nums">
          {filtered
            ? `${total.toLocaleString()} of ${rowsLabel(rowsCount)}`
            : rowsLabel(rowsCount)}
          {filtered && (
            <Button
              variant="link"
              size="xs"
              className="h-auto p-0"
              onClick={onShowAll}
            >
              Show all
            </Button>
          )}
        </span>
        {hasMore && (
          <Button
            variant="ghost"
            size="xs"
            loading={loadingMore}
            onClick={onLoadMore}
          >
            Load more
          </Button>
        )}
      </Stack>
    </Frame>
  );
};
